<template>
    <li
        class="pb2-outline-node"
        role="treeitem"
        :aria-level="level"
        :aria-selected="isSelected"
        :aria-expanded="block.children?.length ? true : undefined"
    >
        <div
            class="pb2-outline-node__row"
            :class="{
                'pb2-outline-node__row--selected': isSelected,
                'pb2-outline-node__row--hovered': isHovered,
                'pb2-outline-node__row--ghost': isGhost,
                'pb2-outline-node__row--drop-before': dropZone === 'before',
                'pb2-outline-node__row--drop-after': dropZone === 'after',
                'pb2-outline-node__row--drop-into': dropZone === 'into',
            }"
            :data-pb2-block-id="id ?? undefined"
            tabindex="0"
            @click="select"
            @contextmenu.prevent="openContextMenu"
            @keydown.enter.prevent="selectByKeyboard"
            @keydown.space.prevent="selectByKeyboard"
            @mouseenter="store.hover(id)"
            @mouseleave="store.hover(null)"
        >
            <span
                v-if="store.mode.value === 'edit' && id !== null"
                class="pb2-outline-node__grip"
                aria-hidden="true"
                @pointerdown.stop="startDrag"
            >
                <Icon icon="phosphor/regular/dots-six-vertical" class="size-3" />
            </span>
            <span class="pb2-outline-node__label">{{ label }}</span>
            <span v-if="isSharedBlock(block)" class="pb2-outline-node__badge">
                {{ t('shared_block') }}
            </span>
            <span v-else-if="isGhost" class="pb2-outline-node__badge">
                {{ t('other_domain') }}
            </span>
            <span v-if="block.hidden" class="pb2-outline-node__badge">
                {{ t('hidden') }}
            </span>
            <span v-if="hiddenBreakpoints" class="pb2-outline-node__badge">
                {{ t('visibility_hidden_on', { breakpoints: hiddenBreakpoints }) }}
            </span>
            <button
                v-if="store.mode.value === 'edit' && id !== null"
                v-tooltip="block.hidden ? t('show_block') : t('hide_block')"
                type="button"
                class="pb2-outline-node__eye"
                :class="{ 'pb2-outline-node__eye--off': block.hidden }"
                :aria-pressed="block.hidden === true"
                :aria-label="block.hidden ? t('show_block') : t('hide_block')"
                @click.stop="store.toggleHidden(id)"
                @keydown.enter.stop
                @keydown.space.stop
                @pointerdown.stop
            >
                <Icon :icon="block.hidden ? 'phosphor/regular/eye-slash' : 'phosphor/regular/eye'" class="size-3.5" />
            </button>
        </div>

        <TransitionGroup
            v-if="block.children?.length"
            tag="ul"
            name="pb2-row"
            class="pb2-outline-node__children"
            role="group"
        >
            <OutlineNode
                v-for="(child, index) in block.children"
                :key="childKey(child, index)"
                :block="child"
                :level="level + 1"
            />
        </TransitionGroup>
    </li>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import { useBuilderT } from '@src/Common/Builder/host';
import { isSharedBlock, markerId } from '@src/Common/Builder/types';
import type { BlockNode } from '@src/Common/Builder/types';
import { useBlockContextMenuOptional } from '@src/Common/Builder/useBlockContextMenu';
import { useBuilderStore } from '@src/Common/Builder/useBuilderStore';
import { useBuilderBridge } from '@src/Common/Builder/useBuilderBridge';
import { useDragMachine } from '@src/Common/Builder/useDragMachine';
import { OUTLINE_DROP_KEY } from '@src/Common/Builder/Outline/outlineDrop';

const t = useBuilderT();

interface Props {
    block: BlockNode;
    level?: number;
}

const props = withDefaults(defineProps<Props>(), { level: 1 });
const level = computed(() => props.level);

const store = useBuilderStore();
const bridge = useBuilderBridge();
const machine = useDragMachine();
const contextMenu = useBlockContextMenuOptional();
const outlineDrop = inject(OUTLINE_DROP_KEY, null);

const id = computed(() => markerId(props.block));
const isSelected = computed(() => id.value !== null && store.selectedIds.value.includes(id.value));
const isHovered = computed(() => id.value !== null && store.hoverId.value === id.value);
const isGhost = computed(() => props.block.domain !== null && props.block.domain !== undefined);

const dropZone = computed(() => {
    if (outlineDrop === null || outlineDrop.value === null || outlineDrop.value.id !== id.value) {
        return null;
    }

    return outlineDrop.value.zone;
});

const label = computed(
    () => (id.value !== null ? store.labels.value[id.value] : undefined) ?? String(props.block.type),
);

/** Translated names of breakpoints the block is hidden on, '' when none. */
const hiddenBreakpoints = computed(() => {
    const visibility = props.block.visibility;

    if (!visibility) {
        return '';
    }

    return (['desktop', 'tablet', 'mobile'] as const)
        .filter((breakpoint) => visibility[breakpoint] === false)
        .map((breakpoint) => t(`breakpoint_${breakpoint}`))
        .join(', ');
});

const select = (event: MouseEvent): void => {
    if (id.value === null) {
        return;
    }

    if (event.metaKey || event.ctrlKey) {
        store.toggleSelect(id.value);
        return;
    }

    if (event.shiftKey) {
        store.rangeSelect(id.value);
        return;
    }

    store.select(id.value);
    bridge.scrollTo(id.value);
};

const selectByKeyboard = (): void => {
    if (id.value === null) {
        return;
    }

    store.select(id.value);
    bridge.scrollTo(id.value);
};

const openContextMenu = (event: MouseEvent): void => {
    if (id.value === null || contextMenu === null || isGhost.value) {
        return;
    }

    contextMenu.open({ x: event.clientX, y: event.clientY }, id.value, { surface: 'outline' });
};

const startDrag = (event: PointerEvent): void => {
    if (id.value === null) {
        return;
    }

    machine.beginPointerDrag(event, { kind: 'move', blockId: id.value }, label.value);
};

/** Stable keys for FLIP; index fallback only for anonymous nodes. */
const childKey = (child: BlockNode, index: number): string => markerId(child) ?? `i-${index}`;
</script>

<style scoped>
.pb2-outline-node {
    list-style: none;
}

/* VS Code Explorer look: flat full-bleed rows, neutral highlight — no
 * rounding, no accent text or bracket on the active item. */
.pb2-outline-node__row {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    width: 100%;
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    text-align: left;
    color: var(--pb2-text, #3f3f46);
    background: none;
    border: 0;
    border-radius: 0;
    cursor: pointer;
}

.pb2-outline-node__row:hover,
.pb2-outline-node__row--hovered {
    background: var(--pb2-surface, #fafafa);
}

.pb2-outline-node__row--selected {
    background: var(--pb2-surface-raised, #ececef);
}

/* Drop edges read as a "gap": thicker bar + a nudge away from the edge —
 * transform-only, so rows never reflow under the pointer mid-drag. */
.pb2-outline-node__row--drop-before {
    box-shadow: inset 0 3px 0 var(--pb2-accent, #2563eb);
    transform: translateY(1px);
}

.pb2-outline-node__row--drop-after {
    box-shadow: inset 0 -3px 0 var(--pb2-accent, #2563eb);
    transform: translateY(-1px);
}

.pb2-outline-node__row--drop-before,
.pb2-outline-node__row--drop-after,
.pb2-outline-node__row--drop-into {
    transition: transform 100ms ease;
}

.pb2-outline-node__row--drop-into {
    outline: 2px dashed var(--pb2-accent, #2563eb);
    outline-offset: -2px;
    background: color-mix(in srgb, var(--pb2-accent, #2563eb) 8%, transparent);
}

@media (prefers-reduced-motion: reduce) {
    .pb2-outline-node__row--drop-before,
    .pb2-outline-node__row--drop-after {
        transform: none;
        transition: none;
    }
}

.pb2-outline-node__row--ghost .pb2-outline-node__label {
    color: var(--pb2-muted, #a1a1aa);
}

.pb2-outline-node__grip {
    flex-shrink: 0;
    color: var(--pb2-muted, #a1a1aa);
    cursor: grab;
    touch-action: none;
}

.pb2-outline-node__label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.pb2-outline-node__badge {
    flex-shrink: 0;
    padding: 0 0.375rem;
    font-size: 0.625rem;
    line-height: 1.5;
    color: var(--pb2-muted, #a1a1aa);
    border: 1px solid var(--pb2-border, #e4e4e7);
    border-radius: 999px;
}

.pb2-outline-node__eye {
    flex-shrink: 0;
    margin-left: auto;
    padding: 0;
    font-size: 0.6875rem;
    line-height: 1;
    color: var(--pb2-muted, #a1a1aa);
    background: none;
    border: 0;
    opacity: 0;
    cursor: pointer;
}

.pb2-outline-node__eye:focus-visible {
    opacity: 1;
}

.pb2-outline-node__row:hover .pb2-outline-node__eye,
.pb2-outline-node__eye--off {
    opacity: 1;
}

.pb2-outline-node__children {
    margin: 0;
    padding-left: 0.875rem;
}
</style>
